
import * as React from "react"

import { cn } from "@/lib/utils"

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, ...props }, ref) => {
    // Create a stable ref to ensure consistent identity across renders
    const stableRef = React.useRef<HTMLTextAreaElement | null>(null);
    
    // Merge refs to support both the forwarded ref and our internal ref
    const handleRef = React.useCallback((node: HTMLTextAreaElement | null) => {
      stableRef.current = node;
      
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    }, [ref]);
    
    // Handle auto-resize when content changes
    const handleInput = React.useCallback((e: React.FormEvent<HTMLTextAreaElement>) => {
      if (props.onInput) {
        props.onInput(e);
      }
      
      // Ensure we're in a browser environment
      if (typeof window === 'undefined') return;
      
      // Only auto-resize if the element has the resize-y class
      if (className?.includes('resize-y') && stableRef.current) {
        stableRef.current.style.height = 'auto';
        stableRef.current.style.height = `${stableRef.current.scrollHeight}px`;
      }
    }, [props.onInput, className]);
    
    return (
      <textarea
        className={cn(
          "flex min-h-[200px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y",
          className
        )}
        ref={handleRef}
        onInput={handleInput}
        {...props}
      />
    )
  }
)
Textarea.displayName = "Textarea"

export { Textarea }
